import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Modal, Form, Row, Col, Button, Badge, Alert, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import LoadingOverlay from "app/orderTool/components/LoadingOverlay";
import HelpTip from "app/orderTool/components/HelpTip";
import DocumentsButton from "app/orderTool/components/DocumentsButton";
import {
  getImpayeDetail,
  getRecouvrementBrouillon,
  postRecouvrementNote,
  patchRecouvrementStatut,
  postRecouvrementRelance,
} from "state/recouvrement/actions";
import { STATUT_LABELS, labelFor, euros } from "./recouvrementLabels";

const CHARGES = ["Julie", "Valentin", "Camille"];
const HUBSPOT_PORTAL = "7856187"; // portail HubSpot Le Moulin (recherche par email/nom)
const hubspotContactUrl = (email) =>
  `https://app.hubspot.com/contacts/${HUBSPOT_PORTAL}/objects/0-1/views/all/list?query=${encodeURIComponent(email)}`;
const hubspotCompanyUrl = (name) =>
  `https://app.hubspot.com/contacts/${HUBSPOT_PORTAL}/objects/0-2/views/all/list?query=${encodeURIComponent(name)}`;

const RecouvrementDetailModal = ({ show, groupedOrderId, impayeCard, constants, onClose, onSaved }) => {
  const dispatch = useDispatch();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState("");
  const [statut, setStatut] = useState("");
  const [prochaineAction, setProchaineAction] = useState("");
  const [charge, setCharge] = useState("");
  const [brouillon, setBrouillon] = useState(null);
  const [brouillonLoading, setBrouillonLoading] = useState(false);

  useEffect(() => {
    if (!show || !groupedOrderId) {
      setDetail(null);
      setBrouillon(null);
      setNote("");
      return;
    }
    loadDetail(groupedOrderId);
  }, [show, groupedOrderId]);

  const loadDetail = async (id) => {
    setLoading(true);
    const resp = await dispatch(getImpayeDetail(id));
    const data = resp?.impaye || impayeCard;
    setDetail(data);
    if (data?.suivi) {
      setStatut(data.suivi.statut || "A_RELANCER");
      setProchaineAction(data.suivi.prochaine_action || "");
      setCharge(data.charge_de_clientele || "");
    }
    setLoading(false);
  };

  const handleAnnoter = async () => {
    if (!note.trim()) {
      toast.error("Saisissez une note");
      return;
    }
    setSaving(true);
    const resp = await dispatch(postRecouvrementNote(groupedOrderId, note.trim()));
    setSaving(false);
    if (resp?.error || resp?.success === false) {
      toast.error(resp?.message || "Erreur lors de l'enregistrement");
      return;
    }
    toast.success("Note enregistrée");
    setNote("");
    await loadDetail(groupedOrderId);
    onSaved?.();
  };

  const handleSaveStatut = async () => {
    setSaving(true);
    const resp = await dispatch(
      patchRecouvrementStatut(groupedOrderId, {
        statut,
        prochaine_action: prochaineAction || null,
        charge_de_clientele: charge || null,
      })
    );
    setSaving(false);
    if (resp?.error || resp?.success === false) {
      toast.error(resp?.message || "Erreur");
      return;
    }
    toast.success("Suivi mis à jour");
    await loadDetail(groupedOrderId);
    onSaved?.();
  };

  const handlePreparerRelance = async () => {
    setBrouillonLoading(true);
    const resp = await dispatch(getRecouvrementBrouillon(groupedOrderId));
    setBrouillonLoading(false);
    if (resp?.brouillon) {
      setBrouillon(resp.brouillon);
    } else {
      toast.error("Impossible de générer le brouillon");
    }
  };

  const handleMarquerRelance = async () => {
    setSaving(true);
    const resp = await dispatch(postRecouvrementRelance(groupedOrderId));
    setSaving(false);
    if (resp?.error || resp?.success === false) {
      toast.error(resp?.message || "Erreur");
      return;
    }
    toast.success("Relance enregistrée");
    if (resp?.brouillon) {
      setBrouillon(resp.brouillon);
    }
    await loadDetail(groupedOrderId);
    onSaved?.();
  };

  const copyBrouillon = () => {
    if (!brouillon) return;
    const text = `Objet : ${brouillon.objet}\n\n${brouillon.corps}`;
    navigator.clipboard?.writeText(text).then(
      () => toast.success("Brouillon copié"),
      () => toast.info("Copiez le texte manuellement")
    );
  };

  const row = detail || impayeCard;

  return (
    <Modal show={show} onHide={onClose} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          {row?.invoice_number || "Facture impayée"}
          {row?.jours_retard != null && (
            <Badge variant="danger" className="ml-2">{row.jours_retard} j de retard</Badge>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ position: "relative", minHeight: 120 }}>
        <LoadingOverlay show={loading} message="Chargement…" />
        {!loading && row && (
          <>
            <Row className="mb-3">
              <Col md={6}>
                <p className="mb-1"><b>Client :</b> {row.client_nom || "—"}</p>
                <p className="mb-1"><b>Entreprise :</b> {row.entreprise_nom || "—"}</p>
                <p className="mb-1"><b>Email :</b> {row.client_email || "—"}</p>
                <div style={{ fontSize: "0.85rem" }}>
                  {row.client_email && (
                    <a href={hubspotContactUrl(row.client_email)} target="_blank" rel="noopener noreferrer" className="mr-2">↗ Fiche contact HubSpot</a>
                  )}
                  {row.entreprise_nom && (
                    <a href={hubspotCompanyUrl(row.entreprise_nom)} target="_blank" rel="noopener noreferrer">↗ Fiche entreprise HubSpot</a>
                  )}
                </div>
              </Col>
              <Col md={6}>
                <p className="mb-1 d-flex align-items-center" style={{ gap: 8 }}>
                  <span><b>Facture :</b> {row.invoice_number || row.order_number}</span>
                  <DocumentsButton documents={row.documents} />
                </p>
                <p className="mb-1"><b>Montant dû TTC :</b> {euros(row.montant_du_centimes)}</p>
                <p className="mb-1">
                  <b>Échéance :</b>{" "}
                  {row.date_echeance ? new Date(row.date_echeance).toLocaleDateString("fr-FR") : "—"}
                </p>
              </Col>
            </Row>

            {row.annulee_sans_avoir && (
              <Alert variant="danger" className="mt-2 mb-0">
                <strong>⚠ Prestation annulée sans avoir</strong>
                {row.annulee_le ? ` (annulée le ${new Date(row.annulee_le).toLocaleDateString("fr-FR")})` : ""} —
                aucune facture d'avoir n'a été émise : la facture reste due tant que
                l'avoir n'est pas créé. À régulariser côté facturation avant de solder le dossier.
              </Alert>
            )}

            <hr />

            <h6>
              Suivi recouvrement
              <HelpTip title="Suivi du recouvrement">
                <p className="mb-0">Notez ici où en est le dossier : <strong>statut</strong> (à relancer, en cours, promesse, litige…), <strong>prochaine action</strong> à mener et son <strong>chargé</strong>. C'est ce qui s'affiche dans la liste pour piloter les relances.</p>
              </HelpTip>
            </h6>
            <Row>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Statut</Form.Label>
                  <Form.Control as="select" value={statut} onChange={(e) => setStatut(e.target.value)}>
                    {(constants?.statuts || Object.keys(STATUT_LABELS)).map((s) => (
                      <option key={s} value={s}>
                        {labelFor(constants?.statuts_labels || STATUT_LABELS, s)}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Prochaine action</Form.Label>
                  <Form.Control
                    type="date"
                    value={prochaineAction}
                    onChange={(e) => setProchaineAction(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Chargé de clientèle</Form.Label>
                  <Form.Control as="select" value={charge} onChange={(e) => setCharge(e.target.value)}>
                    <option value="">—</option>
                    {CHARGES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
            </Row>
            <Button size="sm" variant="success" className="mb-3" onClick={handleSaveStatut} disabled={saving}>
              {saving ? <Spinner animation="border" size="sm" /> : "Enregistrer le suivi"}
            </Button>

            <hr />

            <h6>Annoter</h6>
            <Form.Group>
              <Form.Control
                as="textarea"
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Note interne (appel client, promesse, litige…)"
              />
            </Form.Group>
            <Button size="sm" variant="outline-secondary" onClick={handleAnnoter} disabled={saving}>
              Ajouter la note
            </Button>

            {row.suivi?.notes?.length > 0 && (
              <div className="mt-3">
                <small className="text-muted">Historique des notes</small>
                <ul className="small mb-0 pl-3">
                  {row.suivi.notes.map((n) => (
                    <li key={n.id}>
                      <b>{n.created_at}</b>
                      {n.auteur ? ` (${n.auteur})` : ""} — {n.contenu}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <hr />

            <h6>
              Relance
              <HelpTip title="Relances">
                <p className="mb-1"><strong>Aucun email n'est envoyé automatiquement.</strong> « Préparer une relance » génère un <strong>brouillon</strong> (objet + texte adaptés au niveau) à copier ou ouvrir dans votre messagerie.</p>
                <p className="mb-0">« Marquer comme relancé » <strong>incrémente le niveau</strong> (1, 2 = de plus en plus ferme) et garde la trace dans l'historique des notes — sans rien envoyer.</p>
              </HelpTip>
            </h6>
            <p className="small text-muted mb-2">
              Niveau actuel : {row.suivi?.niveau_relance ?? 0} — aucun envoi automatique, brouillon copiable uniquement.
            </p>
            <div className="d-flex flex-wrap mb-2">
              <Button
                size="sm"
                variant="outline-primary"
                className="mr-2 mb-1"
                onClick={handlePreparerRelance}
                disabled={brouillonLoading}
              >
                {brouillonLoading ? <Spinner animation="border" size="sm" /> : "Préparer une relance"}
              </Button>
              <Button
                size="sm"
                variant="primary"
                className="mr-2 mb-1"
                onClick={handleMarquerRelance}
                disabled={saving}
              >
                Marquer comme relancé
              </Button>
              {brouillon?.mailto && (
                <Button size="sm" variant="link" className="mb-1" href={brouillon.mailto}>
                  Ouvrir mailto
                </Button>
              )}
            </div>

            {brouillon && (
              <Alert variant="light" className="small">
                <p className="mb-1"><b>Objet :</b> {brouillon.objet}</p>
                <pre style={{ whiteSpace: "pre-wrap", fontSize: "0.85rem" }}>{brouillon.corps}</pre>
                <Button size="sm" variant="outline-secondary" onClick={copyBrouillon}>
                  Copier le brouillon
                </Button>
              </Alert>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Fermer</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RecouvrementDetailModal;
